import React from 'react';
import { Row, Col, Form } from 'react-bootstrap';
import { formatPhoneNumber } from 'services/UtilityService';
import { POPULATION_TYPE_OPTIONS, LANGUAGE_OPTIONS } from '../../../lookup/PopulationTypeConstants';
import { ConfigFormLabel, ConfigFormSwitch, ConfigSection } from './ConfigFormComponents';
import LogoUrlField from './LogoUrlField';

const SelectPlaceholder = ({ children }) => (
  <option value="">{children}</option>
);

export const renderFaxTemplateOptions = (faxTemplates) => {
  if (!faxTemplates || typeof faxTemplates !== 'object' || Array.isArray(faxTemplates)) {
    return null;
  }
  return Object.entries(faxTemplates).map(([key, template]) => {
    const displayName =
      typeof template === 'object' && template !== null && template.displayName
        ? template.displayName
        : typeof template === 'string'
        ? template
        : key;
    const isDisabled = typeof template === 'object' && template !== null && template.disabled === true;
    const displayText = isDisabled ? `${displayName} - Deprecated` : displayName;
    return (
      <option key={key} value={key} disabled={isDisabled}>
        {displayText}
      </option>
    );
  });
};

export const CLIENT_FORM_SECTIONS = [
  { id: 'client-identity', label: 'Identity' },
  { id: 'client-contact', label: 'Contact' },
  { id: 'client-call-activities', label: 'Call Activities' },
  { id: 'client-programs', label: 'Programs' },
  { id: 'client-other', label: 'Other' },
  { id: 'client-pricing', label: 'Pricing' },
  { id: 'client-stars', label: 'STARS' },
  { id: 'client-program-defaults', label: 'Program defaults' },
];

export const ClientFormSections = ({
  formState,
  errors = {},
  onInputChange,
  onCheckboxChange,
  onBlur,
  shouldShowError,
  faxTemplates,
  loadingFaxTemplates = false,
  isReadOnly = false,
  sensitiveFields = [],
  disabledFields = {},
  showValidation = false,
}) => {
  const isSwitchDisabled = (fieldName) =>
    isReadOnly ||
    disabledFields.callActivities ||
    fieldName === 'prescriberCallActivitiesEnabled' ||
    fieldName === 'pharmacyCallActivitiesEnabled';

  const showFieldError = (fieldName) => {
    if (typeof shouldShowError === 'function') {
      return shouldShowError(fieldName);
    }
    return showValidation && !!errors[fieldName];
  };

  return (
    <>
      <ConfigSection id="client-identity" title="Identity">
        <Row>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Client Name <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                name="clientName"
                value={formState.clientName}
                onChange={onInputChange}
                onBlur={onBlur}
                disabled={isReadOnly || disabledFields.clientName}
                isInvalid={showFieldError('clientName')}
              />
              {showFieldError('clientName') && (
                <Form.Control.Feedback type="invalid">{errors.clientName}</Form.Control.Feedback>
              )}
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Program Name <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                name="programName"
                value={formState.programName}
                onChange={onInputChange}
                onBlur={onBlur}
                disabled={isReadOnly || disabledFields.programName}
                isInvalid={showFieldError('programName')}
              />
              {showFieldError('programName') && (
                <Form.Control.Feedback type="invalid">{errors.programName}</Form.Control.Feedback>
              )}
            </Form.Group>
          </Col>
          <Col md={6} className="mt-2">
            <Form.Group>
              <Form.Label>Client Abbreviation <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                name="clientNameAbbreviation"
                value={formState.clientNameAbbreviation}
                onChange={onInputChange}
                onBlur={onBlur}
                disabled={isReadOnly || disabledFields.clientNameAbbreviation}
                isInvalid={showFieldError('clientNameAbbreviation')}
              />
              {showFieldError('clientNameAbbreviation') && (
                <Form.Control.Feedback type="invalid">{errors.clientNameAbbreviation}</Form.Control.Feedback>
              )}
            </Form.Group>
          </Col>
          <Col md={6} className="mt-2">
            <LogoUrlField
              value={formState.logoUrl}
              onChange={onInputChange}
              onBlur={onBlur}
              disabled={isReadOnly}
              sensitiveFields={sensitiveFields}
              required
              isInvalid={showFieldError('logoUrl')}
              error={errors.logoUrl}
            />
          </Col>
          {formState.customerName !== undefined && (
            <Col md={6} className="mt-2">
              <Form.Group>
                <Form.Label>Customer Name</Form.Label>
                <Form.Control type="text" value={formState.customerName} disabled />
              </Form.Group>
            </Col>
          )}
        </Row>
      </ConfigSection>

      <ConfigSection id="client-contact" title="Contact">
        <Row>
          <Col md={6}>
            <Form.Group>
              <ConfigFormLabel label="Support Number" fieldName="supportNumber" sensitiveFields={sensitiveFields} />
              <Form.Control
                type="text"
                name="supportNumber"
                value={formatPhoneNumber(formState.supportNumber || '')}
                onChange={onInputChange}
                onBlur={onBlur}
                placeholder="e.g. (555) 555-5555"
                maxLength={14}
                disabled={isReadOnly}
                isInvalid={showFieldError('supportNumber')}
              />
              {showFieldError('supportNumber') && (
                <Form.Control.Feedback type="invalid">{errors.supportNumber}</Form.Control.Feedback>
              )}
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <ConfigFormLabel label="Inbound Fax Number" fieldName="faxNumberInbound" sensitiveFields={sensitiveFields} />
              <Form.Control
                type="text"
                name="faxNumberInbound"
                value={formatPhoneNumber(formState.faxNumberInbound || '')}
                onChange={onInputChange}
                onBlur={onBlur}
                placeholder="e.g. (555) 555-5555"
                maxLength={14}
                disabled={isReadOnly}
                isInvalid={showFieldError('faxNumberInbound')}
              />
              {showFieldError('faxNumberInbound') && (
                <Form.Control.Feedback type="invalid">{errors.faxNumberInbound}</Form.Control.Feedback>
              )}
            </Form.Group>
          </Col>
        </Row>
      </ConfigSection>

      <ConfigSection id="client-call-activities" title="Call Activities">
        <Row>
          <Col md={6}>
            <ConfigFormSwitch
              label="Prescribers"
              fieldName="prescriberCallActivitiesEnabled"
              checked={formState.prescriberCallActivitiesEnabled}
              onChange={onCheckboxChange}
              id="prescriber-call-activities-switch"
              disabled={isSwitchDisabled('prescriberCallActivitiesEnabled')}
              sensitiveFields={sensitiveFields}
            />
          </Col>
          <Col md={6}>
            <ConfigFormSwitch
              label="Pharmacies"
              fieldName="pharmacyCallActivitiesEnabled"
              checked={formState.pharmacyCallActivitiesEnabled}
              onChange={onCheckboxChange}
              id="pharmacy-call-activities-switch"
              disabled={isSwitchDisabled('pharmacyCallActivitiesEnabled')}
              sensitiveFields={sensitiveFields}
            />
          </Col>
        </Row>
      </ConfigSection>

      <ConfigSection id="client-programs" title="Programs">
        <Row>
          <Col md={6}>
            <ConfigFormSwitch label="Polypharmacy" fieldName="polypharmacyEnabled" checked={formState.polypharmacyEnabled} onChange={onCheckboxChange} id="polypharmacy-switch" disabled={isReadOnly} sensitiveFields={sensitiveFields} />
          </Col>
          <Col md={6}>
            <ConfigFormSwitch label="Deprescription Confirmations" fieldName="deprescriptionConfirmationEnabled" checked={formState.deprescriptionConfirmationEnabled} onChange={onCheckboxChange} id="deprescription-confirmation-switch" disabled={isReadOnly} sensitiveFields={sensitiveFields} />
          </Col>
        </Row>
      </ConfigSection>

      <ConfigSection id="client-other" title="Other">
        <Row>
          <Col md={6}>
            <ConfigFormSwitch label="Custom Switches" fieldName="hasCustomSwitches" checked={formState.hasCustomSwitches} onChange={onCheckboxChange} id="custom-switches-switch" disabled={isReadOnly} sensitiveFields={sensitiveFields} />
          </Col>
          <Col md={6}>
            <ConfigFormSwitch label="Prescriber Emails" fieldName="isPrescriberEmailEnabled" checked={formState.isPrescriberEmailEnabled} onChange={onCheckboxChange} id="prescriber-email-switch" disabled={isReadOnly} sensitiveFields={sensitiveFields} />
          </Col>
        </Row>
        <Row className="mt-2">
          <Col md={6}>
            <ConfigFormSwitch label="RX Advocate" fieldName="isRxAdvocateEnabled" checked={formState.isRxAdvocateEnabled} onChange={onCheckboxChange} id="rx-advocate-enabled-switch" disabled={isReadOnly} sensitiveFields={sensitiveFields} />
          </Col>
        </Row>
      </ConfigSection>

      <ConfigSection id="client-pricing" title="Pricing">
        <Row>
          <Col md={6}>
            <ConfigFormSwitch label="Use Price Factors" fieldName="useNvpSourcePricing" checked={formState.useNvpSourcePricing} onChange={onCheckboxChange} id="use-nvp-source-pricing-switch" disabled={isReadOnly} sensitiveFields={sensitiveFields} />
          </Col>
        </Row>
      </ConfigSection>

      <ConfigSection id="client-stars" title="STARS">
        <Row>
          <Col md={6}>
            <ConfigFormSwitch label="Exclude STARS Medications" fieldName="excludeStarSwitches" checked={formState.excludeStarSwitches} onChange={onCheckboxChange} id="exclude-star-switches-switch" disabled={isReadOnly} sensitiveFields={sensitiveFields} />
          </Col>
          <Col md={6}>
            <ConfigFormSwitch label="Hide Deprescribe Suggestions" fieldName="excludeStarDeprescriptions" checked={formState.excludeStarDeprescriptions} onChange={onCheckboxChange} id="exclude-star-deprescriptions-switch" disabled={isReadOnly} sensitiveFields={sensitiveFields} />
          </Col>
        </Row>
      </ConfigSection>

      <ConfigSection id="client-program-defaults" title="Program defaults">
        <Row>
          <Col md={4}>
            <Form.Group>
              <ConfigFormLabel label={<>Language <span className="text-danger">*</span></>} fieldName="language" sensitiveFields={sensitiveFields} />
              <Form.Select name="language" value={formState.language || ''} onChange={onInputChange} onBlur={onBlur} disabled={isReadOnly} isInvalid={showFieldError('language')}>
                <SelectPlaceholder>Select a language</SelectPlaceholder>
                {LANGUAGE_OPTIONS.map(({ value, label }) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </Form.Select>
              {showFieldError('language') && (
                <Form.Control.Feedback type="invalid">{errors.language}</Form.Control.Feedback>
              )}
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <ConfigFormLabel label={<>Fax Template <span className="text-danger">*</span></>} fieldName="faxTemplate" sensitiveFields={sensitiveFields} />
              <Form.Select name="faxTemplate" value={formState.faxTemplate || ''} onChange={onInputChange} onBlur={onBlur} disabled={isReadOnly} isInvalid={showFieldError('faxTemplate')}>
                <SelectPlaceholder>Select a template</SelectPlaceholder>
                {renderFaxTemplateOptions(faxTemplates)}
                {!loadingFaxTemplates && faxTemplates && Object.keys(faxTemplates).length === 0 && (
                  <option value="" disabled>No templates available</option>
                )}
              </Form.Select>
              {showFieldError('faxTemplate') && (
                <Form.Control.Feedback type="invalid">{errors.faxTemplate}</Form.Control.Feedback>
              )}
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <ConfigFormLabel label={<>Population Type <span className="text-danger">*</span></>} fieldName="populationType" sensitiveFields={sensitiveFields} />
              <Form.Select name="populationType" value={formState.populationType || ''} onChange={onInputChange} onBlur={onBlur} disabled={isReadOnly} isInvalid={showFieldError('populationType')}>
                <SelectPlaceholder>Select population type</SelectPlaceholder>
                {POPULATION_TYPE_OPTIONS.map(({ value, label }) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </Form.Select>
              {showFieldError('populationType') && (
                <Form.Control.Feedback type="invalid">{errors.populationType}</Form.Control.Feedback>
              )}
            </Form.Group>
          </Col>
        </Row>
      </ConfigSection>
    </>
  );
};
